// chat/Chat.tsx — the in-app "App Brain" chat for Oriora Chores. Type to add a chore, log
// one done, rename / re-point / retire, search, or get the (live) weekly recap — no clicking.
// Delete is Human-In-The-Loop: held in `pending` and only run on an explicit Confirm tap.
// Scoped to the active member passed in via props (chores + completions are per-member).

import { useState, useRef, useEffect } from "react";
import { askBrain, type ChatTurn } from "./brain";
import { MicButton, SpeakButton } from "./voice";
import { PhotoButton, PhotoChip } from "./photo";
import { store } from "../store";
import { cloudBackupStatus } from "../brain";
import CallOverlay from "../live/CallOverlay";
import { platform } from "../tauri-web";
import { setVoiceScope } from "./voiceTools";
import { executeAction, type ChatAction, type ChatCtx, type FilterRow } from "./actions";

const STORE_KEY = "oriora.oriora_chores.chat.v1";
const GREETING: ChatTurn = {
  role: "assistant",
  content: 'Tell me what to do — I\'ll add a chore, log one done, tweak it, find it, or recap the week. No clicking.',
};

function loadChat(): ChatTurn[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) { const p = JSON.parse(raw); if (Array.isArray(p) && p.length) return p; }
  } catch { /* ignore */ }
  return [GREETING];
}
function saveChat(turns: ChatTurn[]) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(turns.slice(-100))); } catch { /* ignore */ }
}

// Destructive actions are NEVER auto-run from a chat turn — they wait for a Confirm tap.
const destructive = (k: ChatAction["kind"]) => k === "delete_chore" || k === "delete_member" || k === "backup_restore" || k === "import_file" ||
        k === "library_remove" || k === "library_merge";
const warnFor = (action: ChatAction): string =>
  
  action.kind === "library_merge"
    ? `Merge ${action.label ?? "these items"} into one? The originals are folded into the new item — tap Confirm, or Cancel.`
    : action.kind === "library_remove"
      ? `Remove ${action.label ?? "that item"} from your library? This can't be undone — tap Confirm, or Cancel.`
      : action.kind === "import_file"
    ? "Importing will REPLACE everything in the app with the file you pick. Tap Confirm to choose a file, or Cancel."
    : action.kind === "backup_restore"
      ? `Restore will REPLACE everything in the app with your ${action.provider === "gdrive" ? "Google Drive" : action.provider === "github" ? "GitHub" : "cloud"} backup. Tap Confirm to proceed, or Cancel.`
      : action.kind === "delete_member"
      ? `Delete ${action.label ?? "this person"} and ALL of their chores and logged entries? Tap Confirm, or Cancel.`
      : `Delete “${action.kind === "delete_chore" ? (action.label ?? "this chore") : "this"}” and its logged entries? Tap Confirm, or Cancel.`;
const tailNote = (rest: ChatAction[]): string =>
  rest.length ? `\n\n(${rest.length} more step${rest.length > 1 ? "s" : ""} from this request will run after you confirm.)` : "";
// A held destructive action PLUS the rest of its queue, so Confirm resumes instead of dropping it.
type PendingHold = { action: ChatAction; rest: ChatAction[] };

export default function Chat({ memberId, memberName, onChanged }: { memberId: string | null; memberName: string | null; onChanged?: () => void }) {
  const [turns, setTurns] = useState<ChatTurn[]>(loadChat);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [onCall, setOnCall] = useState(false);
  const [pending, setPending] = useState<PendingHold | null>(null);
  // A photo picked in the composer, waiting to be filed onto a chore completion. Never sent to the model.
  const [photo, setPhoto] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const ctx: ChatCtx = { memberId, memberName };

  // Keep the live call pointed at the same person the screen is showing. Without this, voice
  // would act with no member scope and every member-scoped verb would refuse.
  useEffect(() => { setVoiceScope({ memberId, memberName }); }, [memberId, memberName]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns, busy, pending]);
  useEffect(() => { saveChat(turns); }, [turns]);

  // A photo turn is filed ON-DEVICE onto a chore completion (proof-of-done): no model call, no cost.
  // The chore is named in the same message ("photo + dishes"); if it's ambiguous we ask which chore.
  async function sendPhoto(text: string) {
    const next: ChatTurn[] = [...turns, { role: "user", content: text || "📷 photo" }];
    setTurns(next); setInput(""); setBusy(true);
    try {
      if (!memberId) {
        setTurns([...next, { role: "assistant", content: "Pick or add a person first — then I'll file the photo onto their chore." }]);
        return; // keep the photo attached
      }
      const chores = await store.listChores(memberId);
      const q = text.trim().toLowerCase();
      const matches = q ? chores.filter((c) => c.name.toLowerCase().includes(q)) : [];
      const target = matches.length === 1 ? matches[0] : null;
      if (!target) {
        const list = chores.filter((c) => c.active !== 0).slice(0, 6).map((c) => `• ${c.name}`).join("\n");
        setTurns([...next, { role: "assistant", content: chores.length
          ? `Which chore is this photo for? Say its name.\n${list}`
          : "Add a chore first, then I'll attach the photo when it's logged done." }]);
        return; // keep the photo attached so the next message can name the chore
      }
      await store.createCompletion({ member_id: memberId, chore_id: target.id, photo: photo! });
      setPhoto(null);
      onChanged?.();
      setTurns([...next, { role: "assistant", content: `📷 Logged “${target.name}” done with your photo.` }]);
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Couldn't file that photo: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  // Run actions in order until the first destructive one, which is returned as a hold with its tail.
  async function runQueue(queue: ChatAction[], execCtx: ChatCtx): Promise<{ lines: string[]; rows?: FilterRow[]; changed: boolean; hold: PendingHold | null }> {
    const lines: string[] = [];
    let rows: FilterRow[] | undefined;
    let changed = false;
    for (let i = 0; i < queue.length; i++) {
      const action = queue[i];
      if (destructive(action.kind)) {
        // RESTORE STORY: the confirm must NAME the source. When the customer didn't name one,
        // resolve the priority source (what the panel's status line shows) before the hold.
        let held = action;
        if (held.kind === "backup_restore" && !held.provider) {
          try {
            const s = await cloudBackupStatus();
            if (s.writes_to) held = { ...held, provider: s.writes_to };
          } catch { /* hold falls back to "cloud" wording */ }
        }
        return { lines, rows, changed, hold: { action: held, rest: queue.slice(i + 1) } };
      }
      const result = await executeAction(action, execCtx);
      if (result.message) lines.push(result.message);
      if (result.rows) rows = result.rows;
      if (result.changed) changed = true;
    }
    return { lines, rows, changed, hold: null };
  }

  async function send() {
    const text = input.trim();
    if (photo) { await sendPhoto(text); return; }   // photo turns never reach the model
    if (!text || busy) return;
    const next: ChatTurn[] = [...turns, { role: "user", content: text }];
    setTurns(next); setInput(""); setBusy(true);
    try {
      // Streaming: show the assistant bubble immediately and grow it with each chunk; every
      // settle path below overwrites it via setTurns([...next, …]) with the authoritative reply.
      let streamed = "";
      const onChunk = (t: string) => { streamed += t; setTurns([...next, { role: "assistant", content: streamed }]); };
      setTurns([...next, { role: "assistant", content: "" }]);
      const { reply, actions } = await askBrain(next, ctx, onChunk);
      const execCtx: ChatCtx = { ...ctx, transcript: next.map((t) => `${t.role}: ${t.content}`).join("\n") };
      // Run every non-destructive action in order — two asks in one message must do BOTH (G17).
      const r = await runQueue(actions, execCtx);
      if (r.changed) onChanged?.();
      if (r.hold) {
        setPending(r.hold);
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines, warnFor(r.hold.action) + tailNote(r.hold.rest)].filter(Boolean).join("\n\n"), rows: r.rows }]);
      } else {
        setTurns([...next, { role: "assistant", content: [reply, ...r.lines].filter(Boolean).join("\n\n") || "Done.", rows: r.rows }]);
      }
    } catch (e) {
      setTurns([...next, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending || busy) return;
    setBusy(true);
    const { action, rest } = pending;
    try {
      const lines: string[] = [];
      let rows: FilterRow[] | undefined;
      const execCtx: ChatCtx = { ...ctx, transcript: turns.map((t) => `${t.role}: ${t.content}`).join("\n") };
      const result = await executeAction(action, execCtx);
      if (result.message) lines.push(result.message);
      if (result.changed) onChanged?.();
      // Resume the rest of the request the held action was blocking.
      const r = await runQueue(rest, execCtx);
      lines.push(...r.lines);
      if (r.rows) rows = r.rows;
      if (r.changed) onChanged?.();
      if (r.hold) { setPending(r.hold); lines.push(warnFor(r.hold.action) + tailNote(r.hold.rest)); }
      else setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: lines.filter(Boolean).join("\n\n") || "Done.", rows }]);
    } catch (e) {
      setPending(null);
      setTurns((t) => [...t, { role: "assistant", content: `Something went wrong: ${e instanceof Error ? e.message : String(e)}` }]);
    } finally {
      setBusy(false);
    }
  }
  function newChat() {
    if (busy) return;
    setPending(null);
    setTurns([GREETING]);
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
  }

  function cancelPending() {
    const skipped = pending?.rest.length ?? 0;
    setPending(null);
    setTurns((t) => [...t, { role: "assistant", content: "Okay — kept it." + (skipped ? ` (Skipped the ${skipped} step${skipped > 1 ? "s" : ""} that were waiting on this.)` : "") }]);
  }

  return (
    <div className="och-chat">
      <div className="och-scroll">
        {turns.length > 1 && (
          <div style={{ textAlign: "center", marginBottom: "0.4rem" }}>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)", opacity: 0.75 }} onClick={newChat} disabled={busy}>
              ↺ Start a new chat
            </button>
          </div>
        )}
        {turns.map((t, i) => (
          <div key={i} className={`och-msgrow ${t.role === "user" ? "me" : ""}`}>
            <div className={`och-bubble ${t.role === "user" ? "me" : "bot"}`}>
              {t.content}
              {t.role !== "user" && t.content.trim() ? (<div style={{ marginTop: 2, textAlign: "right" }}><SpeakButton text={t.content} /></div>) : null}
              {t.rows && t.rows.length > 0 && <FilterRows rows={t.rows} />}
            </div>
          </div>
        ))}
        {busy && <div className="och-msgrow"><div className="och-bubble bot thinking">thinking</div></div>}
        <div ref={endRef} />
      </div>
      {pending ? (
        <div className="och-composer och-confirm">
          <button className="btn btn-primary" style={{ background: "var(--rose)", borderColor: "var(--rose)" }} onClick={confirmPending} disabled={busy}>{pending && String(pending.action.kind).startsWith("delete") ? "Confirm delete" : "Confirm"}</button>
          <button className="btn" onClick={cancelPending} disabled={busy}>Cancel</button>
        </div>
      ) : (
        <>
          {photo && <PhotoChip src={photo} onClear={() => setPhoto(null)} />}
          <div className="och-composer">
            <textarea
              value={input}
              placeholder={photo ? "Name the chore, or just send…" : "Tell me what to do…"}
              rows={2}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            />
            <PhotoButton onPhoto={setPhoto} disabled={busy} />
            <MicButton value={input} onChange={setInput} disabled={busy} />
            {/* Every surface. iOS runs the native voice plugin; macOS and web use the browser's
                own audio (getUserMedia + its echo canceller) — the stack every desktop voice
                assistant uses. */}
            {platform() === "ios" && <button
              type="button"
              className="btn btn-pill"
              title="Talk to it live"
              aria-label="Start a live call"
              style={{ flexShrink: 0, fontSize: 16 }}
              onClick={() => setOnCall(true)}
            >📞</button>}
            {/* Mounted right beside the button on purpose: CallOverlay is a fixed-position sheet,
                so it renders correctly anywhere in the tree, and this needs no structural analysis
                of each app's JSX — a button whose overlay never got mounted is the half-wired
                failure this roll exists to avoid. */}
            {onCall && <CallOverlay
              onClose={() => setOnCall(false)}
              onTranscript={(callLines, seconds) => {
                const mm = Math.floor(seconds / 60), ss = String(seconds % 60).padStart(2, "0");
                setTurns((prev) => [
                  ...prev,
                  { role: "assistant" as const, content: `📞 Live call · ${mm}:${ss}` },
                  ...callLines.map((l) => ({
                    role: l.role === "you" ? ("user" as const) : ("assistant" as const),
                    content: l.text.trim(),
                  })),
                ]);
              }}
            />}
            <button className="btn btn-primary" onClick={send} disabled={busy}>Send</button>
          </div>
        </>
      )}
    </div>
  );
}

function FilterRows({ rows }: { rows: FilterRow[] }) {
  return (
    <div className="och-rows">
      {rows.map((r) => (
        <div key={r.id} className="och-rowitem">
          <span>{r.name}{!r.active && " (retired)"}</span>
          <span className="och-num">{r.status !== "—" ? `${r.status} · ` : ""}{r.points} pt{r.points === 1 ? "" : "s"}</span>
        </div>
      ))}
    </div>
  );
}
